import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { 
  UserGroupIcon,
  StarIcon,
  WrenchScrewdriverIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CheckCircleIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import api from '../services/api';
import useStatusBarHeight from '../hooks/useStatusBarHeight';
import RatingModal from '../components/RatingModal';
import useAuthStore from '../store/authStore';

const ContractorList = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const [ratingModal, setRatingModal] = useState({ isOpen: false, contractorId: null, contractorName: '' });
  const statusBarHeight = useStatusBarHeight();
  const { user } = useAuthStore();

  // 소장 목록 조회
  const { data: contractors, isLoading } = useQuery(
    ['contractors', searchQuery, selectedSpecialty],
    () => api.get('/contractors', {
      params: {
        search: searchQuery,
        specialty: selectedSpecialty
      }
    }).then(res => res.data)
  );

  // 공사 유형 목록
  const { data: settings } = useQuery(
    'settings',
    () => api.get('/settings').then(res => res.data)
  );

  const constructionTypes = settings?.construction_types?.split(',').map(type => type.trim()) || [];

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => {
      if (i < Math.floor(rating)) {
        return <StarIconSolid key={i} className="h-4 w-4 text-yellow-400" />;
      } else {
        return <StarIcon key={i} className="h-4 w-4 text-gray-300" />;
      }
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="animate-pulse">
          <div className="bg-white px-4 py-6">
            <div className="h-8 bg-gray-200 rounded-lg w-32 mb-2"></div>
          </div>
          <div className="p-4 space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl p-5 shadow-sm">
                <div className="h-6 bg-gray-200 rounded-lg w-3/4 mb-3"></div>
                <div className="h-4 bg-gray-200 rounded-lg w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-white sticky z-20 shadow-sm" style={{ top: `${statusBarHeight}px` }}>
        <div className="px-4 py-4">
          <div className="flex items-center gap-3 mb-4">
            <UserGroupIcon className="h-7 w-7 text-blue-600" />
            <h1 className="text-xl font-bold text-gray-900">소장님 찾기</h1>
          </div>

          {/* 검색바 */}
          <div className="relative mb-3">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="소장님 이름 또는 회사명 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* 필터 버튼 */}
          <button
            onClick={() => setShowFilter(!showFilter)}
            className={`w-full px-4 py-2 rounded-xl flex items-center justify-center gap-2 transition-all ${
              showFilter || selectedSpecialty ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            <FunnelIcon className="h-4 w-4" />
            <span className="text-sm font-medium">
              {selectedSpecialty || '전문 분야 필터'}
            </span>
          </button>

          {/* 필터 옵션 */}
          {showFilter && (
            <div className="mt-3 p-3 bg-gray-50 rounded-xl animate-slide-down">
              <p className="text-sm font-medium text-gray-700 mb-2">전문 분야 선택</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    setSelectedSpecialty('');
                    setShowFilter(false);
                  }}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    !selectedSpecialty ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 border border-gray-300'
                  }`}
                >
                  전체
                </button>
                {constructionTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => {
                      setSelectedSpecialty(type);
                      setShowFilter(false);
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      selectedSpecialty === type ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 border border-gray-300'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 소장 목록 */}
      <div className="p-4">
        {contractors && contractors.length > 0 ? (
          <div className="space-y-3">
            {contractors.map((contractor) => (
              <div
                key={contractor.id}
                className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all p-4 border border-gray-200 hover:border-gray-300"
              >
                <div className="flex items-start gap-4">
                  {/* 프로필 이미지 */}
                  <Link to={`/contractors/${contractor.id}`} className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-xl">
                        {contractor.name.charAt(0)}
                      </span>
                    </div>
                  </Link>

                  {/* 정보 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <Link to={`/contractors/${contractor.id}`} className="group">
                        <h3 className="text-base font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                          {contractor.name} 소장님
                        </h3>
                      </Link>
                      <div className="flex items-center gap-2">
                        {contractor.projectCount > 50 && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1">
                            <CheckCircleIcon className="h-3 w-3" />
                            우수
                          </span>
                        )}
                        {user && ['customer', 'vip_customer'].includes(user.user_type) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setRatingModal({
                                isOpen: true,
                                contractorId: contractor.id,
                                contractorName: contractor.name
                              });
                            }}
                            className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors"
                          >
                            <PencilSquareIcon className="h-3.5 w-3.5" />
                            평가하기
                          </button>
                        )}
                      </div>
                    </div>
                      
                      <p className="text-sm text-gray-600 mb-2">{contractor.company_name}</p>

                      {/* 평점 */}
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex items-center gap-0.5">
                          {renderStars(contractor.rating || 0)}
                        </div>
                        <span className="text-sm text-gray-600">
                          {contractor.rating?.toFixed(1) || '0.0'} 
                          {contractor.reviewCount > 0 && ` (${contractor.reviewCount})`}
                        </span>
                      </div>

                      {/* 전문 분야 */}
                      {contractor.specialties && (
                        <div className="flex items-center gap-1.5 mb-2">
                          <WrenchScrewdriverIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <div className="flex flex-wrap gap-1">
                            {contractor.specialties.split(',').slice(0, 3).map((specialty, index) => (
                              <span
                                key={index}
                                className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md text-xs font-medium"
                              >
                                {specialty.trim()}
                              </span>
                            ))}
                            {contractor.specialties.split(',').length > 3 && (
                              <span className="text-xs text-gray-500">
                                +{contractor.specialties.split(',').length - 3}
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* 통계 */}
                      <div className="flex items-center gap-4 text-xs text-gray-600">
                        <span>완료 {contractor.completedCount || 0}건</span>
                        <span>•</span>
                        <span>진행중 {contractor.progressCount || 0}건</span>
                        {contractor.completionRate && (
                          <>
                            <span>•</span>
                            <span className="text-green-600 font-medium">
                              성공률 {contractor.completionRate}%
                            </span>
                          </>
                        )}
                      </div>
                  </div>

                  {/* 화살표 */}
                  <Link to={`/contractors/${contractor.id}`}>
                    <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-1" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20">
            <UserGroupIcon className="h-16 w-16 text-gray-300 mb-4" />
            <p className="text-gray-500 font-medium mb-2">소장님을 찾을 수 없습니다</p>
            <p className="text-sm text-gray-400">다른 검색어를 시도해보세요</p>
          </div>
        )}
      </div>

      {/* 평가 모달 */}
      <RatingModal
        isOpen={ratingModal.isOpen}
        onClose={() => setRatingModal({ isOpen: false, contractorId: null, contractorName: '' })}
        contractorId={ratingModal.contractorId}
        contractorName={ratingModal.contractorName}
      />
    </div>
  );
};

export default ContractorList;